import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { Info } from '@/assets/icons'

interface ApplePaySafariWarningProps {
  onContinue?: () => void
  onCancel?: () => void
}

/**
 * Компонент предупреждения о необходимости открыть в Safari для Apple Pay
 * Показывается когда пользователь выбрал Apple Pay в Telegram WebView
 * После нажатия "Continue" откроется новое окно с Apple Pay
 */
export const ApplePaySafariWarning: React.FC<ApplePaySafariWarningProps> = ({
  onContinue,
  onCancel,
}) => {
  const { t } = useTranslation()

  return (
    <BaseView
      bgColor="background.card"
      borderRadius={16}
      p={16}
      mb={16}
      style={{
        border: '1px solid rgba(255, 159, 10, 0.3)',
        backgroundColor: 'rgba(255, 159, 10, 0.1)',
      }}
    >
      {/* Иконка */}
      <BaseView align="center" mb={12}>
        <BaseView
          bgColor="rgba(255, 159, 10, 0.2)"
          borderRadius={50}
          style={{
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Info width={24} height={24} fill="#FF9F0A" />
        </BaseView>
      </BaseView>

      {/* Заголовок */}
      <BaseText
        fontSize={16}
        fontFamily="semiBold"
        color="label.primary"
        textAlign="center"
        mb={8}
      >
        {t('common:applePayRequiresSafari', 'Apple Pay требует Safari')}
      </BaseText>

      {/* Описание */}
      <BaseText
        fontSize={14}
        color="label.secondary"
        textAlign="center"
        mb={16}
        style={{ lineHeight: 1.5 }}
      >
        {t(
          'common:applePaySafariDescription',
          'Apple Pay will open in a new window where you can complete your payment securely. You will be redirected back after payment.',
        )}
      </BaseText>

      {/* Кнопки */}
      <BaseView gap={8}>
        <AppButton
          label={t('common:continue', 'Continue')}
          onPress={onContinue}
          variant="primary"
        />
        {onCancel && (
          <AppButton
            label={t('form:actions.cancel', 'Cancel')}
            onPress={onCancel}
            variant="secondary"
          />
        )}
      </BaseView>

      {/* Подсказка */}
      <BaseText
        fontSize={12}
        color="label.tertiary"
        textAlign="center"
        mt={12}
        style={{ lineHeight: 1.4 }}
      >
        {t(
          'common:applePaySafariHint',
          'Make sure to allow popups for this site to use Apple Pay',
        )}
      </BaseText>
    </BaseView>
  )
}
